import React from "react";
import { useNavigate } from "react-router";
import {
  Box,
  Button,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import InsightsIcon from "@mui/icons-material/Insights";
import SecurityIcon from "@mui/icons-material/Security";
import PublicIcon from "@mui/icons-material/Public";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import LinkIcon from "@mui/icons-material/Link";
import nrsLogo from "../assets/NRS-Logo.webp";
import sophiaciLogo from "../assets/sophiaci_logo.jpeg";
import hero_img from "../assets/mining-platform-about-header.jpg";

const titleDividerSx = { width: 48, height: 2, backgroundColor: "primary.main", mt: 1.5, mb: 3 };
const titleSx = {
  fontWeight: 800,
  lineHeight: 1.14,
  letterSpacing: "-0.3px",
  mb: 1.5,
  fontSize: { xs: "1.85rem", md: "2.3rem" },
};
const bodySx = {
  color: "text.secondary",
  lineHeight: 1.75,
  fontSize: { xs: "1rem", md: "1.05rem" },
};
const cardTitleSx = { fontWeight: 700, mb: 1 };
const cardHeaderSx = { fontWeight: 700, lineHeight: 1.3 };
const cardBodySx = { color: "text.secondary", lineHeight: 1.7 };
const SECTION_PY = { xs: 8, md: 12 };
const sectionKickerSx = { color: "primary.main", letterSpacing: 1.2, textTransform: "uppercase", mb: 1.5 };

const bulletList = (items) => (
  <List dense sx={{ py: 0 }}>
    {items.map((text) => (
      <ListItem key={text} sx={{ alignItems: "flex-start", py: 0.25 }}>
        <ListItemIcon sx={{ minWidth: 28, color: "primary.main", display: "flex", pt: 0.5 }}>
          <CheckCircleIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          sx={{ mt: 0 }}
          primary={
            <Typography variant="body2" sx={cardBodySx}>
              {text}
            </Typography>
          }
        />
      </ListItem>
    ))}
  </List>
);

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <Box>
      <Box
        sx={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.48), rgba(0,0,0,0.28)),url(${hero_img})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          py: 20,
          px: 3,
          color: "white",
          position: "relative",
        }}
      >
        <Container maxWidth={false} sx={{ position: "relative", zIndex: 1, maxWidth: "lg" }}>
          <Stack spacing={2} maxWidth={920}>
            <Typography variant="h3" fontWeight={800}>
              Why Mining Intelligence Needs a Common Language
            </Typography>
            <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.92)" }}>
              ResourceNAV standardizes company, asset, financial, and market intelligence so mining equities can be compared with clarity and context.
            </Typography>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth={false} sx={{ maxWidth: "lg", py: { xs: 7, md: 9 }, px: { xs: 2, md: 3 } }}>
        <Box sx={{ mt: "25px" }}>
          <Section
            title="The Mining Data Fragmentation Problem"
            paragraphs={[
              "Mining information is abundant, but it is rarely easy to compare. Company disclosures vary across filings, technical reports, presentations, tables, units, asset structures, and commodity conventions.",
              "Investors and analysts often have to rebuild the same dataset manually before they can answer basic questions: what does this company own, where are its assets located, what stage is it in, how does it compare, and what metrics actually matter?",
              "Raw disclosures are valuable, but without structure they create friction. ResourceNAV exists to turn that raw information into comparable intelligence.",
            ]}
          />
        </Box>

        <Box sx={{ py: { xs: 6, md: 8 }, mb: { xs: 8, md: 11 } }}>
          <Box
            sx={{
              background: "linear-gradient(180deg, rgba(255,255,255,0.78), rgba(255,255,255,0.44))",
              borderRadius: { xs: 5, md: 8 },
              px: { xs: 2.5, md: 4 },
              py: { xs: 4.5, md: 8 },
              mx: "auto",
              maxWidth: { xs: "100%", md: 1260 },
              boxShadow: "0 24px 60px rgba(0,0,0,0.08)",
            }}
          >
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography variant="overline" sx={sectionKickerSx}>
                STANDARDIZATION
              </Typography>
              <Typography variant="h5" sx={{ ...titleSx, mb: 1.5 }}>
                Why Standardization Matters
              </Typography>
              <Box sx={{ ...titleDividerSx, mx: "auto", mt: 0, mb: 3 }} />
              <Typography variant="body1" sx={{ ...bodySx, maxWidth: 900, mx: "auto" }}>
                Mining companies cannot be evaluated like a generic sector screen. Lifecycle stage, commodity exposure, jurisdiction, asset ownership, resource quality, financial position, and market context all shape the investment picture. ResourceNAV brings those dimensions into a consistent analytical framework.
              </Typography>
            </Box>
            <Grid container spacing={3} sx={{ mt: 3 }}>
              {[
                {
                  title: "Comparable Company Profiles",
                  text: "Lifecycle, commodity, exchange, headquarters, and company attributes are organized into a common structure.",
                },
                {
                  title: "Asset-Level Context",
                  text: "Projects, resources, jurisdictions, ownership, production, and forecast information are modeled beneath the company level.",
                },
                {
                  title: "Financial and Market Context",
                  text: "Standardized financial and market metrics help connect operating narratives to investable comparisons.",
                },
              ].map((item, index) => (
                <Grid size={{ xs: 12, md: 4 }} key={item.title}>
                  <Paper
                    elevation={3}
                    sx={{ p: 4, borderRadius: 3, height: "100%", borderTop: index === 0 ? "2px solid #d9b352" : undefined }}
                  >
                    <Typography variant="h6" sx={cardTitleSx}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={cardBodySx}>
                      {item.text}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        <Section
          kicker="OUR PHILOSOPHY"
          title="ResourceNAV's Philosophy"
          paragraphs={[
            "ResourceNAV is not simply cataloging mining companies. It is building a standardized analytical foundation where company, asset, financial, and market intelligence can be compared with clarity, consistency, and context.",
            "The platform is designed for investors and analysts who need to move from discovery to comparison quickly, without losing the nuance that makes mining equities different from other sectors.",
          ]}
          customContent={
            <Grid container spacing={2.5} sx={{ mt: 1 }}>
              {[
                "Mining data speaks a common language",
                "Decisions are grounded in structured context",
                "Comparisons are transparent and objective",
                "Insights emerge from signal, not noise",
              ].map((text) => (
                <Grid size={{ xs: 12, sm: 6 }} key={text}>
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <CheckCircleIcon fontSize="small" sx={{ color: "primary.main", mt: "4px" }} />
                    <Typography variant="body1" sx={{ ...bodySx, color: "text.primary", fontWeight: 600 }}>
                      {text}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          }
        />

        <Box sx={{ py: SECTION_PY }}>
          <Typography variant="overline" sx={{ color: "primary.main", letterSpacing: 1.2, textTransform: "uppercase" }}>
            METHODOLOGY
          </Typography>
          <Typography variant="h5" sx={{ ...titleSx, mb: 1.5 }}>
            How the Platform Turns Disclosures into Intelligence
          </Typography>
          <Box sx={{ ...titleDividerSx, mx: 0, mt: 0, mb: 2 }} />
          <Grid container spacing={3}>
            {[
              {
                heading: "Structure the Universe",
                items: [
                  "Map companies, assets, commodities, exchanges, jurisdictions, and lifecycle classifications.",
                  "Standardize profile data so discovery starts from a consistent company universe.",
                ],
                icon: <PublicIcon color="primary" />,
              },
              {
                heading: "Normalize the Data",
                items: [
                  "Model resources, reserves, production, financials, and market metrics using consistent fields.",
                  "Preserve source context while making the data usable for comparison and screening.",
                ],
                icon: <MenuBookIcon color="primary" />,
              },
              {
                heading: "Surface Decision Context",
                items: [
                  "Turn standardized data into rankings, filters, scoring, and comparison workflows.",
                  "Help users discover companies that match a thesis, commodity view, jurisdiction preference, or lifecycle mandate.",
                ],
                icon: <AutoGraphIcon color="primary" />,
              },
            ].map((col, index) => (
              <Grid size={{ xs: 12, md: 4 }} key={col.heading}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    textAlign: "left",
                    borderRadius: 3,
                    height: "100%",
                    borderTop: index === 0 ? "2px solid #d9b352" : undefined,
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>{col.icon}</Box>
                    <Typography variant="h6" sx={cardHeaderSx}>
                      {col.heading}
                    </Typography>
                  </Stack>
                  {bulletList(col.items)}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Typography
          variant="overline"
          sx={{ ...sectionKickerSx, mt: { xs: 8, md: 11 }, display: "block" }}
        >
          BENEFITS
        </Typography>
        <ValuePanel
          title="Benefits for Investors"
          intro="ResourceNAV equips investors with a structured view of the mining landscape, from company discovery through rankings and advanced screening."
          ctaLabel="Explore Rankings"
          onCta={() => navigate("/rankings")}
          columns={[
            {
              heading: "Advanced Discovery",
              items: [
                "Standardized profiles and comparable metrics to screen quickly.",
                "Curated rankings that surface companies across key mining dimensions.",
                "Screener workflows for building a custom investment universe.",
              ],
              icon: <AutoGraphIcon color="primary" />,
            },
            {
              heading: "Better Context",
              items: [
                "Company, asset, jurisdiction, resource, financial, and market data in one framework.",
                "Less time rebuilding source data before analysis can begin.",
              ],
              icon: <InsightsIcon color="primary" />,
            },
            {
              heading: "Current Information",
              items: [
                "Claimed profiles can improve accuracy and update speed.",
                "Platform data evolves as company disclosures and market context change.",
              ],
              icon: <SecurityIcon color="primary" />,
            },
          ]}
        />

        <ValuePanel
          title="Benefits for Miners"
          intro="ResourceNAV helps mining companies improve visibility, credibility, and investor engagement inside a platform built for mining-specific comparison."
          ctaLabel="Claim Your Company"
          onCta={() => navigate("/benefits")}
          columns={[
            {
              heading: "Improve Visibility",
              items: [
                "Increase reach by improving how investors discover and compare your company.",
                "Surface when investor criteria aligns with your profile and performance.",
              ],
              icon: <PublicIcon color="primary" />,
            },
            {
              heading: "Own Your Narrative",
              items: [
                "Claim your profile to keep information accurate, current, and verified.",
                "Stand out through structured profiles and objective rankings.",
              ],
              icon: <InsightsIcon color="primary" />,
            },
            {
              heading: "Modernize the Story",
              items: [
                "Bring transparency and clarity to one of the world's oldest industries.",
                "Communicate assets, resources, operations, and forecasts in a comparable framework.",
              ],
              icon: <MenuBookIcon color="primary" />,
            },
          ]}
        />

        <Box sx={{ mt: { xs: 8, md: 11 }, mb: 1 }}>
          <Typography
            variant="overline"
            sx={{ color: "primary.main", letterSpacing: 1.2, textTransform: "uppercase", mb: 1 }}
          >
            POWERING THE PLATFORM
          </Typography>
          <Typography variant="h5" sx={{ ...titleSx, mb: 1.5 }}>
            The Companies Behind The Platform
          </Typography>
          <Box sx={{ ...titleDividerSx, mx: 0, mt: 0, mb: 2 }} />
          <Typography variant="body1" sx={{ ...bodySx, mt: 0 }}>
            ResourceNAV is brought to you by the partnership between NaturalResourceStocks.net and SOPHIACI.
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <PartnerCard
                name="NaturalResourceStocks.net"
                highlight
                body={[
                  "NaturalResourceStocks.net is one of the most established and influential platforms in the natural resources investment space.",
                  "With a large, engaged audience of investors, analysts, and industry participants, NaturalResourceStocks.net has built a trusted ecosystem through its website and media channels.",
                ]}
                footer="Their reach and credibility make them a cornerstone voice in the mining investment community, helping investors stay informed across commodities, jurisdictions, and market cycles."
                actions={[
                  { label: "Visit Website", href: "https://www.naturalresourcestocks.net", icon: <LinkIcon fontSize="small" /> },
                  { label: "YouTube Channel", href: "https://www.youtube.com/channel/UCQ7FskN3gIfW21yF8WBLa0A", icon: <PlayCircleOutlineIcon fontSize="small" /> },
                ]}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <PartnerCard
                name="SOPHIACI"
                body={[
                  "SOPHIACI brings deep experience in financial services, analytics, and data-driven platforms.",
                  "The team has spent years building analytical systems that transform complex datasets into decision-ready insights across regulated, data-intensive industries.",
                  "That same rigor is being applied to the mining sector, modernizing how information is structured, analyzed, and communicated.",
                ]}
                footer="The result is a platform that blends financial discipline with industry-specific intelligence, built for the next generation of mining investors and operators."
                actions={[{ label: "Visit Website", href: "https://sophiaci.com", icon: <LinkIcon fontSize="small" /> }]}
              />
            </Grid>
          </Grid>
        </Box>
      </Container>

      <Box
        sx={{
          mt: 6,
          position: "relative",
          overflow: "hidden",
          py: { xs: 7, md: 10 },
          px: { xs: 3, md: 6 },
          textAlign: "center",
          background: "linear-gradient(120deg, rgba(38,38,38,0.92), rgba(201, 176, 123,0.9))",
          color: "white",
        }}
      >
        <Typography variant="h4" fontWeight={900} sx={{ mb: 2, position: "relative", zIndex: 1 }}>
          Explore ResourceNAV
        </Typography>
        <Typography
          variant="body1"
          sx={{
            maxWidth: 760,
            mx: "auto",
            mb: 3,
            lineHeight: 1.7,
            color: "rgba(255,255,255,0.85)",
            position: "relative",
            zIndex: 1,
          }}
        >
          Discover companies, compare rankings, and build screens around the mining factors that matter to your thesis.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center" sx={{ position: "relative", zIndex: 1 }}>
          <Button variant="contained" size="large" onClick={() => navigate("/rankings")} sx={{ px: 5 }}>
            Explore Rankings
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate("/screener")}
            sx={{ px: 5, color: "white", borderColor: "rgba(255,255,255,0.75)" }}
          >
            Open Screener
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}

function Section({ kicker = null, title, paragraphs = [], customContent = null }) {
  return (
    <Box sx={{ mb: { xs: 8, md: 11 } }}>
      {kicker && (
        <Typography variant="overline" sx={sectionKickerSx}>
          {kicker}
        </Typography>
      )}
      {title && (
        <Typography variant="h5" sx={titleSx}>
          {title}
        </Typography>
      )}
      {title && <Box sx={{ ...titleDividerSx, mx: 0 }} />}
      <Stack spacing={2.25} sx={{ maxWidth: 860 }}>
        {paragraphs.map((p) => (
          <Typography key={p} variant="body1" sx={bodySx}>
            {p}
          </Typography>
        ))}
        {customContent}
      </Stack>
    </Box>
  );
}

function ValuePanel({ title, intro, columns, ctaLabel, onCta }) {
  return (
    <>
      <Box>
        <Typography variant="h5" sx={titleSx}>
          {title}
        </Typography>
        <Box sx={{ ...titleDividerSx, mx: 0 }} />
        <Typography variant="body1" sx={{ ...bodySx, mt: 2 }}>
          {intro}
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mt: 3 }}>
        {columns.map((col, index) => (
          <Grid size={{ xs: 12, md: 4 }} key={col.heading}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                textAlign: "left",
                borderRadius: 3,
                height: "100%",
                borderTop: index === 0 ? "2px solid #d9b352" : undefined,
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>{col.icon}</Box>
                <Typography variant="h6" sx={cardHeaderSx}>
                  {col.heading}
                </Typography>
              </Stack>
              {bulletList(col.items)}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 3, mb: 6, display: "flex", justifyContent: { xs: "flex-start", md: "center" } }}>
        <Button variant="contained" onClick={onCta} sx={{ px: 5 }}>
          {ctaLabel}
        </Button>
      </Box>
    </>
  );
}

function PartnerCard({ name, body = [], footer, actions = [], highlight = false }) {
  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        borderRadius: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        textAlign: "left",
        borderTop: highlight ? "2px solid #d9b352" : undefined,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        {name === "NaturalResourceStocks.net" && (
          <Box component="img" src={nrsLogo} alt="NRS logo" sx={{ width: 40, height: 40 }} />
        )}
        {name === "SOPHIACI" && (
          <Box component="img" src={sophiaciLogo} alt="Sophiaci logo" sx={{ width: 40, height: 40 }} />
        )}
        <Typography variant="h6" fontWeight={800}>
          {name}
        </Typography>
      </Stack>
      <Stack spacing={1.2} sx={{ mb: 2 }}>
        {body.map((p) => (
          <Typography key={p} variant="body2" sx={cardBodySx}>
            {p}
          </Typography>
        ))}
        {footer && (
          <Typography variant="body2" sx={cardBodySx}>
            {footer}
          </Typography>
        )}
      </Stack>
      {actions.length > 0 && (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: "auto", flexWrap: "wrap", alignItems: "flex-start" }}>
          {actions.map((action) => (
            <Button
              key={action.label}
              variant="contained"
              startIcon={action.icon}
              component="a"
              href={action.href}
              target="_blank"
              rel="noreferrer"
            >
              {action.label}
            </Button>
          ))}
        </Stack>
      )}
    </Paper>
  );
}
